import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Settings, LogOut, Search, Package, BarChart3 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { BrandLogo } from "../../components/BrandLogo";
import { adminApi, money } from "../../lib/api";

const payLabel = (s) => ({ paid: "Paid", pending: "Awaiting Payment", awaiting_zelle: "Awaiting Zelle", refunded: "Refunded", failed: "Failed", expired: "Expired" }[s] || s);
const payColor = (s) => ({ paid: "hsl(var(--success))", pending: "hsl(var(--warning))", awaiting_zelle: "hsl(var(--warning))", refunded: "hsl(var(--muted-foreground))" }[s] || "hsl(var(--muted-foreground))");
const prodBg = (s) => ({ "Order Received": "rgba(214,229,240,0.7)", "In Production": "rgba(201,169,110,0.35)", "Ready to Ship": "rgba(183,110,121,0.22)", "Shipped": "rgba(31,122,90,0.18)" }[s] || "hsl(var(--muted))");
const firstName = (n) => ((n || "").trim().split(/\s+/)[0] || "Customer");
const shortNum = (on) => `#${(on || "").split("-").pop() || ""}`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payFilter, setPayFilter] = useState("");
  const [q, setQ] = useState("");
  const qRef = useRef(q);
  useEffect(() => { qRef.current = q; }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (payFilter) params.payment = payFilter;
      if (qRef.current) params.q = qRef.current;
      const r = await adminApi.get("/admin/orders", { params });
      setOrders(r.data.orders);
    } catch (e) { if (e?.response?.status === 401) navigate("/admin/login"); }
    finally { setLoading(false); }
  }, [payFilter, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("mm_admin_token")) { navigate("/admin/login"); return; }
    load();
  }, [load, navigate]);
  const logout = () => { localStorage.removeItem("mm_admin_token"); navigate("/admin/login"); };
  const tabs = [{ k: "", l: "All" }, { k: "paid", l: "Paid" }, { k: "pending", l: "Pending" }, { k: "awaiting_zelle", l: "Zelle" }];

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-40 border-b bg-[hsl(var(--background))]/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <BrandLogo size={36} withText />
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/admin/analytics")} className="h-9 rounded-full border bg-white px-3" data-testid="admin-analytics-link"><BarChart3 className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={() => navigate("/admin/settings")} className="h-9 rounded-full border bg-white px-3" data-testid="admin-settings-link"><Settings className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={logout} className="h-9 rounded-full border bg-white px-3"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-3xl px-4 py-5">
        <h1 className="font-display text-2xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>Orders</h1>
        <div className="mt-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by number, name or email" className="h-11 rounded-xl pl-9" data-testid="admin-orders-search-input" />
          </div>
          <Button onClick={load} className="h-11 rounded-xl">Search</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {tabs.map((tb) => (
            <button key={tb.k} onClick={() => setPayFilter(tb.k)} className={`rounded-full px-4 py-1.5 text-sm font-medium ${payFilter === tb.k ? "text-white" : "border bg-white"}`} style={payFilter === tb.k ? { background: "hsl(var(--brand-rose))" } : {}}>{tb.l}</button>
          ))}
        </div>
        <div className="mt-4 space-y-3" data-testid="admin-orders-table">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          : orders.length === 0 ? <div className="flex flex-col items-center gap-2 py-12 text-center text-[hsl(var(--muted-foreground))]"><Package className="h-8 w-8" /><p className="text-sm">No orders yet.</p></div>
          : orders.map((o) => (
            <button key={o.id} data-testid="admin-order-row" onClick={() => navigate(`/admin/orders/${o.id}`)} className="flex w-full items-center gap-3 rounded-xl border bg-card p-4 text-left shadow-[var(--shadow-card)] hover:bg-[hsl(var(--muted))]">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {!o.viewed && (
                    <span data-testid="order-new-badge" className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm" style={{ background: "hsl(var(--brand-rose))" }}>New</span>
                  )}
                  <span className="font-display text-base font-semibold" style={{ color: "hsl(var(--brand-brown))" }} data-testid="order-customer-name">{firstName(o.customer?.name)}</span>
                  <span className="font-display text-sm font-medium text-[hsl(var(--muted-foreground))]" data-testid="order-short-number">{shortNum(o.order_number)}</span>
                  <Badge variant="outline" style={{ color: payColor(o.payment_status), borderColor: payColor(o.payment_status) }} className="rounded-full text-[10px]">{payLabel(o.payment_status)}</Badge>
                  {o.payment_method === "zelle" && <Badge variant="secondary" className="rounded-full text-[10px]">Zelle</Badge>}
                  {o.production_status && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: prodBg(o.production_status) }}>{o.production_status}</span>}
                  {!o.photos_submitted && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: "rgba(201,169,110,0.3)" }} data-testid="order-photos-badge">Photos {o.photo_count_uploaded ?? 0}/{o.photo_count_required ?? o.kit_qty}</span>
                  )}
                </div>
                <div className="truncate text-sm text-[hsl(var(--muted-foreground))]">{o.kit_qty} magnets · {o.city}, {o.country}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold tabular-nums">{money(o.total_cents, (o.currency || "usd").toUpperCase())}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{new Date(o.created_at).toLocaleDateString()}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
